import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import { MULTIPART_PART_SIZE_BYTES, RESUME_RECORD_TTL_MS } from './constants';
import type { MultipartResumeRecord } from './types';

/**
 * Tracks multipart uploads that are partway through, so an upload the network killed can carry on
 * from the part it reached instead of re-sending the whole file.
 *
 * Records hold metadata only, never the file itself - a multi GB video would be written to disk a
 * second time and can blow the origin's storage quota. Resuming therefore needs the same file
 * handed back in, which is why the id is a fingerprint the caller can rebuild from a File.
 *
 * Storage is best effort throughout: private browsing, a full disk or a blocked upgrade all surface
 * as a rejected IndexedDB request, and none of them are a reason to fail an upload that is
 * otherwise working. Every export here swallows its own errors.
 */

const DB_NAME = 'multipart-uploads';
const DB_VERSION = 1;
const STORE_NAME = 'in-progress';

interface MultipartUploadDB extends DBSchema {
  [STORE_NAME]: {
    key: string;
    value: MultipartResumeRecord;
    indexes: { channelId: number };
  };
}

let dbPromise: Promise<IDBPDatabase<MultipartUploadDB>> | undefined;

function getDb(): Promise<IDBPDatabase<MultipartUploadDB>> {
  dbPromise ??= openDB<MultipartUploadDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('channelId', 'channelId');
    },
  }).catch((e) => {
    // don't leave a rejected promise cached here - an upgrade blocked by another tab, or any other
    // one off failure, would otherwise stop this page resuming anything for the rest of its life
    dbPromise = undefined;
    throw e;
  });

  return dbPromise;
}

/**
 * Identifies a file well enough to match it to an upload it already started. Name, size and mtime
 * are what the browser gives us for free - a content hash would mean reading several GB before the
 * upload could even begin, and these three are enough to catch a different file being picked.
 */
export function resumeIdFor(channelId: number, file: File): string {
  return `${channelId}:${file.name}:${file.size}:${file.lastModified}`;
}

/**
 * Whether a record still describes something worth resuming. A record with no parts is no better
 * than starting over, and reusing its upload id would only add a way to go wrong.
 */
function isUsable(record: MultipartResumeRecord): boolean {
  return (
    record.uploadedParts.length > 0 &&
    record.partSize === MULTIPART_PART_SIZE_BYTES &&
    Date.now() - record.updatedAt < RESUME_RECORD_TTL_MS
  );
}

/** Drops records the API will have already reaped. Cheap, so it runs on every lookup. */
export async function pruneResumableUploads(): Promise<void> {
  try {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');

    for (const record of await tx.store.getAll()) {
      if (Date.now() - record.updatedAt >= RESUME_RECORD_TTL_MS) {
        await tx.store.delete(record.id);
      }
    }

    await tx.done;
  } catch (e) {
    console.error('Could not prune resumable uploads', e);
  }
}

/**
 * The upload this file was partway through, if there is one still worth continuing. Anything stale
 * or cut with a different part size is deleted rather than returned.
 */
export async function findResumableUpload(channelId: number, file: File): Promise<MultipartResumeRecord | undefined> {
  void pruneResumableUploads();

  try {
    const db = await getDb();
    const record = await db.get(STORE_NAME, resumeIdFor(channelId, file));

    if (!record) return undefined;

    if (!isUsable(record)) {
      await deleteResumableUpload(record.id);
      return undefined;
    }

    return record;
  } catch (e) {
    console.error('Could not look up a resumable upload', e);
    return undefined;
  }
}

/**
 * Every resumable upload on a channel, newest first. For offering the user a list of uploads to
 * pick back up - each still needs its file handed back in before it can continue.
 */
export async function listResumableUploads(channelId: number): Promise<MultipartResumeRecord[]> {
  await pruneResumableUploads();

  try {
    const db = await getDb();
    const records = await db.getAllFromIndex(STORE_NAME, 'channelId', channelId);

    return records.filter(isUsable).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (e) {
    console.error('Could not list resumable uploads', e);
    return [];
  }
}

export async function saveResumableUpload(record: MultipartResumeRecord): Promise<void> {
  try {
    const db = await getDb();
    await db.put(STORE_NAME, record);
  } catch (e) {
    console.error('Could not save upload progress', e);
  }
}

export async function deleteResumableUpload(id: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(STORE_NAME, id);
  } catch (e) {
    console.error('Could not clear a resumable upload', e);
  }
}
